import Vue from "vue";
import api from "@/api";

import { SESSION_STATES, QUESTION_TYPES, STORAGE_TOKEN_NAME } from "@/config/survey-answer-session";
import i18n from "@/i18n";

/**
 * STATE
 */
const state = {
    survey: null,
    surveyNotification: null,
    state: SESSION_STATES.INACTIVE,
    activeStepIndex: 0,
    activeSection: null,
    loading: true,
    error: null,
    // hashmap to save user responses per question
    userAnswers: {},
};

/**
 * GETTERS
 */
const getters = {
    error(state) {
        return state.error;
    },
    survey(state) {
        return Object.freeze({ ...state.survey });
    },
    surveyNotification(state) {
        return Object.freeze({ ...state.surveyNotification });
    },
    activeSection(state) {
        return state.activeSection ?? {};
    },
    state(state) {
        return state.state;
    },
    isAnonymousSurvey(_, getters) {
        return !!getters.survey.settings?.notAnonymous === false;
    },
    isFullExportEnabled(_, getters) {
        return getters.survey.settings?.fullExportEnabled === true;
    },
    loading(state) {
        return !!state.loading;
    },
    steps(_, getters) {
        return Array.isArray(getters.survey?.questions) ? getters.survey.questions : [];
    },
    activeStep(state, getters) {
        return getters.steps[state.activeStepIndex];
    },
    maximumStepIndex(_, getters) {
        return getters.steps.length - 1;
    },
    interactiveSteps(_, getters) {
        // sections are defined as separate "questions" but should not be part of the answer flow
        return getters.steps.filter(question => question.skill.type !== QUESTION_TYPES.SECTION);
    },
    maximumInteractiveStepIndex(_, getters) {
        return getters.interactiveSteps.length - 1;
    },
    activeInteractiveStepNumber(_, getters) {
        return (
            getters.interactiveSteps.findIndex(
                step => getters.activeStep?.skill._id === step.skill._id,
            ) + 1
        );
    },
    lastInteractiveStepNumber(_, getters) {
        return getters.interactiveSteps.length;
    },
    userAnswers(state) {
        return Object.freeze({ ...state.userAnswers });
    },
    canSkipQuestion(_, getters) {
        return !getters.activeStep?.isRequired;
    },
    canConfirmAnswer(_, getters) {
        return !!getters.userAnswers?.[getters.activeStep?.skill._id] || getters.canSkipQuestion;
    },
    hasGivenAnswer(_, getters) {
        return !!getters.userAnswers?.[getters.activeStep?.skill._id];
    },
};

/**
 * ACTIONS
 */
const actions = {
    init({ state, commit, getters }) {
        let payload = window.localStorage.getItem(STORAGE_TOKEN_NAME);

        if (!payload) return;

        payload = JSON.parse(payload);

        // opened another survey
        if (
            payload.surveyNotificationId !== state.surveyNotification?._id ||
            payload.surveyId !== state.survey?._id
        ) {
            window.localStorage.removeItem(STORAGE_TOKEN_NAME);
            return;
        }

        // change in survey happened, better reset and start from step 0
        // while we keep valid responses
        if (
            payload.activeStepIndex > getters.steps.length - 1 ||
            (payload.surveyQuestionAmount &&
                payload.surveyQuestionAmount !== getters.steps.length) ||
            (payload.surveyQuestionOrder &&
                payload.surveyQuestionOrder?.[payload.activeStepIndex] !==
                    getters.steps?.[payload.activeStepIndex]?.skill?._id)
        ) {
            payload.activeStepIndex = 0;
            payload.activeSection = null;
            payload.sessionState = SESSION_STATES.INACTIVE;
        }

        commit("setInitialState", payload);
    },
    navigateToNextStep(
        { state, getters, commit, dispatch },
        { payload: { direction = 1 } } = { payload: {} },
    ) {
        let nextIndex = 0;

        // init session if not done yet
        if (state.state === SESSION_STATES.INACTIVE) {
            commit("setSurveyState", SESSION_STATES.ACTIVE);
        } else {
            if (direction === -1) {
                // previous
                if (state.activeStepIndex > 0) {
                    nextIndex = state.activeStepIndex - 1;
                }
            } else {
                // next
                if (state.activeStepIndex < getters.maximumStepIndex) {
                    nextIndex = state.activeStepIndex + 1;
                }
            }
        }

        commit("setActiveStep", nextIndex);

        // skip sections
        if (
            nextIndex < getters.maximumStepIndex &&
            getters.steps?.[nextIndex]?.skill?.type === QUESTION_TYPES.SECTION
        ) {
            // we store the latest section name
            if (direction === 1) {
                commit("setActiveSection", { name: getters.steps?.[nextIndex]?.skill?.name });
            } else {
                let checkIndex = nextIndex;

                while (checkIndex >= 0) {
                    const isSection =
                        getters.steps?.[checkIndex]?.skill?.type === QUESTION_TYPES.SECTION;
                    if (isSection) {
                        commit("setActiveSection", {
                            name: getters.steps?.[checkIndex]?.skill?.name,
                        });
                    } else {
                        commit("setActiveSection");
                    }
                    checkIndex -= 1;
                }
            }

            dispatch("navigateToNextStep", { payload: { direction } });
        }
    },
    async getSurveyDetails(
        { commit },
        { payload: { surveyId, notificationId, lang } } = { payload: {} },
    ) {
        commit("setLoadingState", true);
        commit("setError", null);

        try {
            const { data } = await api.get(
                `/api/surveys/get/notification/${surveyId}/${notificationId}?lang=${lang}`,
            );

            // API does not use HTTP status for errors
            if (!data?.success) {
                commit(
                    "setError",
                    data?.error || i18n.t("surveys.participate.unknownDataLoadError"),
                );

                return;
            }

            commit("setSurveyDetails", data);
        } catch (error) {
            commit("setError", error.message ?? i18n.t("surveys.participate.unknownDataLoadError"));
        } finally {
            commit("setLoadingState", false);
        }
    },
    async submitAnswers({ commit }, { payload } = {}) {
        await api.post("/api/feedback/create/noauth", payload);

        // reset state
        commit("setInitialState", { sessionState: SESSION_STATES.INACTIVE });
        commit("removeLocalStorageValue");
    },
    // pass-through action, changes state only
    setAnswer({ commit }, { payload } = {}) {
        commit("setAnswer", payload);
    },
};

/**
 * MUTATIONS
 */
const mutations = {
    setInitialState(
        state,
        {
            activeStepIndex = 0,
            activeSection = null,
            userAnswers = {},
            sessionState = SESSION_STATES.ACTIVE,
        } = {},
    ) {
        state.activeStepIndex = activeStepIndex;
        state.activeSection = activeSection;
        state.userAnswers = userAnswers;
        state.state = sessionState;
    },
    setSurveyDetails(state, { survey, notification } = {}) {
        state.survey = survey;
        state.surveyNotification = notification;
    },
    setSurveyState(state, surveyState = SESSION_STATES.INACTIVE) {
        state.state = surveyState;
    },
    setActiveStep(state, index = 0) {
        state.activeStepIndex = index;
        this.commit("surveys/answerSession/setLocalStorageValue");
    },
    setLoadingState(state, loading = false) {
        state.loading = loading;
    },
    setError(state, error = null) {
        state.error = error;
    },
    resetAnswers(state) {
        state.userAnswers = {};
    },
    removeLocalStorageValue() {
        window.localStorage.removeItem(STORAGE_TOKEN_NAME);
    },
    setLocalStorageValue(state) {
        const questions = Array.isArray(state.survey?.questions) ? state.survey.questions : [];

        // save to restore unfinished sessions
        // - keep only one by intention and for security to not gather details on shared machine
        window.localStorage.setItem(
            STORAGE_TOKEN_NAME,
            JSON.stringify({
                surveyNotificationId: state.surveyNotification?._id,
                surveyId: state.survey?._id,
                activeStepIndex: state.activeStepIndex,
                activeSection: state.activeSection,
                userAnswers: state.userAnswers,
                surveyQuestionOrder: questions.map(item => item.skill._id).filter(Boolean),
                surveyQuestionAmount: questions.length,
            }),
        );
    },
    setAnswer(state, { questionId, answer } = {}) {
        if (!questionId) return;
        if (!answer || !Object.keys(answer).length) {
            Vue.delete(state.userAnswers, questionId);
        } else {
            Vue.set(state.userAnswers, questionId, answer);
        }

        this.commit("surveys/answerSession/setLocalStorageValue");
    },
    setActiveSection(state, { name } = {}) {
        if (!name) state.activeSection = null;

        state.activeSection = {
            name,
        };
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
